import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiClient, Meeting, Recording } from "./api";

export interface RecordingMeetingsState {
  meetingsByRecording: Record<string, Meeting[]>;
  loading: boolean;
  refresh: () => void;
}

/**
 * One `listMeetings` call per recording, so the recordings navigator can nest each
 * recording's meetings underneath it and the search bar can match a meeting's name
 * as well as a recording's.
 *
 * Fetched once per recording id rather than polled — a meeting's name changes only
 * when somebody here changes it, and `refresh` is for after that happens.
 */
export function useRecordingMeetings(api: ApiClient, recordings: Recording[]): RecordingMeetingsState {
  const [meetingsByRecording, setMeetingsByRecording] = useState<Record<string, Meeting[]>>({});
  const [loading, setLoading] = useState(false);
  const [reloads, setReloads] = useState(0);
  const fetched = useRef<Set<string>>(new Set());

  const ids = recordings.map((recording) => recording.id).join(",");

  useEffect(() => {
    const due = recordings.filter((recording) => !fetched.current.has(recording.id));
    if (due.length === 0) return;

    let cancelled = false;
    setLoading(true);

    void Promise.all(
      due.map(async (recording) => {
        try {
          return [recording.id, await api.listMeetings(recording.id)] as const;
        } catch {
          return [recording.id, [] as Meeting[]] as const;
        }
      }),
    ).then((pairs) => {
      for (const [id] of pairs) fetched.current.add(id);
      if (cancelled) return;

      setMeetingsByRecording((current) => {
        const next = { ...current };
        for (const [id, meetings] of pairs) next[id] = meetings;
        return next;
      });
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `ids` stands in for `recordings`.
  }, [api, ids, reloads]);

  const refresh = useCallback(() => {
    fetched.current.clear();
    setReloads((n) => n + 1);
  }, []);

  return { meetingsByRecording, loading, refresh };
}
